import React, { useEffect, useMemo, useState } from "react";
import {
  OBJECTIVE_TYPE,
  REVIEW_STATUS,
  objectiveKey,
} from "../../_config/quarterlyOkrConfig";
import QokrConfirmDelete from "../../_shared/QokrConfirmDelete/QokrConfirmDelete";
import liveReviewStore, {
  isEditorRole,
} from "../_socket/quarterlyOkrLiveReviewSocketStore";
import LiveObjectiveRow from "../LiveObjectiveRow/LiveObjectiveRow";
import LiveObjectiveEditor from "../LiveObjectiveEditor/LiveObjectiveEditor";
import LiveLockDialog from "../LiveLockDialog/LiveLockDialog";
import {
  allIndividuals,
  buildLiveAgenda,
  objectiveComments,
} from "./liveAgenda";
import "../../../ExecutionBlueprint/ExecutionBlueprintLiveReview/ExecutionBlueprintLiveReview.scss";
import "../../../AnnualOkr/_shared/_objective-details.scss";
import "../../_shared/_qokr-tabs.scss";
import "../../_shared/_qokr-buttons.scss";
import "../../_shared/_qokr-forms.scss";
import "./_live-review-body.scss";

// ═══════════════════════════════════════════════════════════════════════════
// The live review session (§10) — the same screen in both apps: EXW's live
// review layout (`eblr-body`: the objectives on the left, the Meeting Agenda
// on the right, Lock in the agenda's footer). Left: Company / Individual tabs
// with one `eblr-row` per objective. Everything shown is the latest
// `qokr_lr_details`; every change is an emit that comes back as a new
// snapshot, so nothing here is applied optimistically.
//
// ⚠️ SHARED WITH THE COACH APP at the same folder depth — change both copies
// together. App differences come in as props: `SummaryGroups`, the tooltip
// and the EXW icon set are each app's own.
// ═══════════════════════════════════════════════════════════════════════════

const TAB = { COMPANY: "company", INDIVIDUAL: "individual" };

const LiveReviewBody = ({
  details,
  role,
  pending,
  draftingUserIds,
  summaryGroupsComponent: SummaryGroups,
  tooltipComponent: Tooltip,
  icons,
}) => {
  const [tab, setTab] = useState(TAB.COMPANY);
  const [expandedKey, setExpandedKey] = useState(null);
  const [editor, setEditor] = useState(null); // { type, objective, defaults }
  const [deletion, setDeletion] = useState(null); // { type, objective, linked }
  const [lockPreflight, setLockPreflight] = useState(undefined);

  const store = liveReviewStore.getState;
  const canEdit = isEditorRole(role);
  const companies = useMemo(() => details?.company_objectives || [], [details]);
  const individuals = useMemo(() => allIndividuals(details), [details]);
  const agenda = useMemo(() => buildLiveAgenda(details), [details]);
  const remaining = details?.to_discuss_remaining || 0;

  const companyLabel = useMemo(
    () => new Map(companies.map((o) => [String(o.objective_id), o.objective])),
    [companies],
  );

  // One person per group on the Individual tab, everyone on the run listed —
  // late joiners with nothing yet get "Draft with AI".
  const people = useMemo(() => {
    const members = details?.members || [];
    return members.map((member) => ({
      member,
      objectives: individuals.filter(
        (o) => String(o.member?.user_id) === String(member.user_id),
      ),
      lateJoiner: (details?.joined_after_submission || []).some(
        (m) => String(m.user_id) === String(member.user_id),
      ),
    }));
  }, [details, individuals]);

  // The first to-discuss objective opens itself once, so the meeting starts
  // on something to talk about.
  useEffect(() => {
    if (expandedKey || !details) return;
    const first = companies.find(
      (o) => o.review?.status === REVIEW_STATUS.TO_DISCUSS,
    );
    if (first)
      setExpandedKey(objectiveKey(OBJECTIVE_TYPE.COMPANY, first.objective_id));
  }, [details]); // eslint-disable-line

  const focus = (key) => {
    const [type] = key.split(":");
    setTab(type === OBJECTIVE_TYPE.COMPANY ? TAB.COMPANY : TAB.INDIVIDUAL);
    setExpandedKey(key);
    requestAnimationFrame(() =>
      document
        .querySelector(`[data-live-target="${key}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" }),
    );
  };

  const annualOptions = useMemo(() => {
    const seen = new Map();
    companies.forEach((o) => {
      if (o.annual_objective_id) seen.set(o.annual_objective_id, o.annual_okr);
    });
    (details?.deferred_annual_okrs || []).forEach((a) =>
      seen.set(a.annual_objective_id, a.statement),
    );
    return [...seen].map(([value, label]) => ({ value, label }));
  }, [companies, details]);

  const companyOptions = useMemo(
    () => companies.map((o) => ({ value: o.objective_id, label: o.objective })),
    [companies],
  );

  const isBusy = (type, objective) =>
    Object.keys(pending).some((k) =>
      k.endsWith(`${type}:${objective.objective_id}`),
    );

  const rowProps = (type, objective, extra = {}) => {
    const key = objectiveKey(type, objective.objective_id);
    return {
      type,
      objective,
      expanded: expandedKey === key,
      onToggle: () => setExpandedKey((open) => (open === key ? null : key)),
      canEdit,
      icons,
      busy: isBusy(type, objective),
      comments: objectiveComments(
        details?.coach_comments || [],
        type,
        objective.objective_id,
      ),
      // EXW's Finalise: one click accepts the objective AND settles it as
      // discussed — never a surprise second step.
      onAccept: async () => {
        const ack = await store().setReviewStatus(
          type,
          objective.objective_id,
          REVIEW_STATUS.ACCEPTED,
        );
        if (ack?.acknowledgement && !objective.review?.discussed_at) {
          store().markDiscussed(type, objective.objective_id);
        }
      },
      onDiscussed: () => store().markDiscussed(type, objective.objective_id),
      onEdit: () => setEditor({ type, objective }),
      // Preview first: a company objective takes its linked individual
      // objectives with it.
      onDelete: async () => {
        const ack = await store().deletePreview(type, objective.objective_id);
        if (ack?.acknowledgement) {
          setDeletion({
            type,
            objective,
            linked: ack.linked_individual_objectives || [],
          });
        }
      },
      ...extra,
    };
  };

  const saveEditor = async (payload) => {
    const ack = await store().saveObjective(payload);
    // `stale_card`: the fresh snapshot is already here — keep the typed text.
    if (ack?.acknowledgement) setEditor(null);
  };

  const confirmDeletion = async () => {
    const ack = await store().deleteObjective(
      deletion.type,
      deletion.objective.objective_id,
    );
    if (ack?.acknowledgement) setDeletion(null);
  };

  const openLock = async () => {
    setLockPreflight(null);
    const ack = await store().lockPreflight();
    setLockPreflight(ack?.acknowledgement ? ack : undefined);
  };

  const lockReason = remaining
    ? `${remaining} objective${remaining === 1 ? "" : "s"} still to discuss`
    : null;

  const lockButton = (
    <button
      type="button"
      className="eblr-complete-btn"
      disabled={Boolean(lockReason)}
      onClick={openLock}
    >
      Lock Quarterly OKRs
    </button>
  );

  return (
    <>
      <div className="eblr-body">
        <div className="eblr-canvas">
          <div className="qokr-tabs" role="tablist" aria-label="OKRs">
            {[
              [TAB.COMPANY, "Company objectives", companies.length],
              [TAB.INDIVIDUAL, "Individual objectives", individuals.length],
            ].map(([key, label, count]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                className={`qokr-tab${tab === key ? " is-active" : ""}`}
                onClick={() => setTab(key)}
              >
                {label}
                <span className="qokr-tab__count">{count}</span>
              </button>
            ))}
          </div>

          {tab === TAB.COMPANY ? (
            <div className="eblr-section">
              <div className="eblr-rows">
                {companies.length ? (
                  companies.map((objective) => (
                    <LiveObjectiveRow
                      key={objectiveKey(
                        OBJECTIVE_TYPE.COMPANY,
                        objective.objective_id,
                      )}
                      {...rowProps(OBJECTIVE_TYPE.COMPANY, objective)}
                    />
                  ))
                ) : (
                  <div className="eblr-row-empty">No company objectives.</div>
                )}
                {canEdit ? (
                  <button
                    type="button"
                    className="qokr-text-btn qokr-lr-body__add"
                    onClick={() =>
                      setEditor({
                        type: OBJECTIVE_TYPE.COMPANY,
                        objective: null,
                      })
                    }
                  >
                    <i className="fa-solid fa-plus" aria-hidden="true" />
                    Add a company objective
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            people.map(({ member, objectives, lateJoiner }) => {
              const drafting = draftingUserIds.includes(String(member.user_id));
              return (
                <div className="eblr-section" key={member.user_id}>
                  <div className="okr-group-head">
                    <span className="okr-group-rail" aria-hidden="true" />
                    <span className="okr-group-name">{member.name}</span>
                    <span className="okr-group-count">{objectives.length}</span>
                  </div>
                  <div className="eblr-rows">
                    {objectives.map((objective) => (
                      <LiveObjectiveRow
                        key={objectiveKey(
                          OBJECTIVE_TYPE.INDIVIDUAL,
                          objective.objective_id,
                        )}
                        {...rowProps(OBJECTIVE_TYPE.INDIVIDUAL, objective, {
                          linkLabel: companyLabel.get(
                            String(objective.company_objective_id),
                          ),
                        })}
                      />
                    ))}
                    {!objectives.length ? (
                      <div className="eblr-row-empty">
                        {lateJoiner
                          ? `${member.name} joined after the send to the coach.`
                          : "No individual objectives."}
                      </div>
                    ) : null}
                    {canEdit ? (
                      <div className="qokr-lr-body__member-actions">
                        <button
                          type="button"
                          className="qokr-text-btn qokr-lr-body__add"
                          onClick={() =>
                            setEditor({
                              type: OBJECTIVE_TYPE.INDIVIDUAL,
                              objective: null,
                              defaults: { userId: member.user_id },
                            })
                          }
                        >
                          <i className="fa-solid fa-plus" aria-hidden="true" />
                          Add an objective for {member.name}
                        </button>
                        {lateJoiner && !objectives.length ? (
                          <button
                            type="button"
                            className="qokr-btn qokr-btn--secondary"
                            disabled={drafting}
                            onClick={() => store().draftMember(member.user_id)}
                          >
                            <i
                              className={`fa-solid ${
                                drafting
                                  ? "fa-spinner fa-spin"
                                  : "fa-wand-magic-sparkles"
                              }`}
                              aria-hidden="true"
                            />
                            {drafting ? "Drafting…" : "Draft with AI"}
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="eblr-agenda">
          <div className="eblr-agenda-header">Meeting Agenda</div>
          <div className="eblr-agenda-body">
            {agenda.length ? (
              <SummaryGroups
                groups={agenda}
                onSelectItem={(item) => {
                  if (/^(company|individual):/.test(String(item.id))) {
                    focus(String(item.id));
                  }
                }}
                selectTitle="Show this objective"
                emptyLabel="Nothing matches this filter."
              />
            ) : (
              <div className="eblr-agenda-empty">
                <span className="eblr-agenda-empty-icon">
                  <i className="fa-solid fa-check" aria-hidden="true" />
                </span>
                <p className="eblr-agenda-empty-title">Nothing to discuss</p>
                <p className="eblr-agenda-empty-text">
                  Nothing was flagged or commented on in the review, so nothing
                  is holding up the lock.
                </p>
              </div>
            )}
          </div>

          <div className="eblr-agenda-footer">
            {!canEdit ? (
              <p className="qokr-lr-body__hint">
                Your CEO or coach locks the quarter when the agenda is done.
              </p>
            ) : lockReason ? (
              // The wrapper takes the hover: a disabled button swallows the
              // events the tooltip listens for.
              <Tooltip tooltTipContent={lockReason}>
                <span className="eblr-complete-btn-wrap">{lockButton}</span>
              </Tooltip>
            ) : (
              lockButton
            )}
          </div>
        </div>
      </div>

      {editor ? (
        <LiveObjectiveEditor
          type={editor.type}
          objective={editor.objective}
          roster={details.roster || []}
          annualOptions={annualOptions}
          companyOptions={companyOptions}
          defaults={editor.defaults}
          saving={Object.keys(pending).some((k) => k.startsWith("save:"))}
          onSave={saveEditor}
          onClose={() => setEditor(null)}
        />
      ) : null}

      {deletion ? (
        <QokrConfirmDelete
          title={`Delete “${deletion.objective.objective}”?`}
          confirmLabel="Delete objective"
          busy={Object.keys(pending).some((k) => k.startsWith("delete:"))}
          onClose={() => setDeletion(null)}
          onConfirm={confirmDeletion}
        >
          {deletion.linked.length ? (
            <>
              <p>These individual objectives are linked to it and go too:</p>
              <ul>
                {deletion.linked.map((item) => (
                  <li key={item.objective_id}>
                    {item.member?.name} · {item.objective}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="mb-0">Its key results and initiatives go with it.</p>
          )}
        </QokrConfirmDelete>
      ) : null}

      {lockPreflight !== undefined ? (
        <LiveLockDialog
          preflight={lockPreflight}
          locking={Boolean(pending.lock)}
          onLock={() => store().lock()}
          onClose={() => setLockPreflight(undefined)}
        />
      ) : null}
    </>
  );
};

export default LiveReviewBody;
